import { FaLightbulb, FaHeart, FaClipboardList, FaBullhorn, FaBookOpen, FaHandsHelping } from 'react-icons/fa';

export const archetypeDescriptions = {
  visionary: {
    id: 'visionary',
    name: 'Visionary Leader',
    icon: FaLightbulb,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    shortDescription: 'Big-picture thinker who inspires with bold ideas',
    fullDescription: "You are a big-picture thinker who excels at inspiring others with bold ideas. You naturally gravitate toward casting vision and dreaming up new ministry initiatives. Your passion for growth and innovation helps push your team beyond the status quo. You see possibilities where others see obstacles, and your enthusiasm is contagious.",
    strengths: [
      'Casting compelling vision for the future',
      'Inspiring and motivating others toward big goals',
      'Thinking creatively and innovatively',
      'Seeing the big picture and long-term potential',
      'Taking calculated risks for greater impact'
    ],
    challenges: [
      'May overlook important details in pursuit of the vision',
      'Can become impatient with slow progress or routine tasks',
      'Might struggle to maintain focus on current responsibilities',
      'May unintentionally overwhelm others with too many new ideas'
    ],
    leadershipTips: [
      'Give them room to dream and share big ideas',
      'Involve them early in planning major changes or new initiatives',
      'Pair them with detail-oriented partners who can execute their vision',
      'Provide opportunities to present vision to larger groups'
    ],
    motivationTips: [
      'Keep them informed of long-term impact and progress',
      'Celebrate big wins and milestones together',
      'Give them new challenges and growth opportunities',
      'Connect their daily work to the larger mission'
    ],
    idealRoles: [
      'Strategic Planning',
      'Church Planting',
      'New Ministry Development',
      'Executive Leadership',
      'Capital Campaigns'
    ],
    communicationDo: [
      'Lead with the big picture and future possibilities',
      'Give them space to brainstorm and dream out loud',
      'Connect tasks to the larger mission and impact',
      'Be direct and concise—they value efficiency'
    ],
    communicationDont: [
      'Overwhelm with details before sharing the vision',
      'Dismiss their ideas without consideration',
      'Focus only on problems without solutions',
      'Be overly rigid about how things must be done'
    ],
    motivationTriggers: [
      'New initiatives and fresh challenges',
      'Seeing their vision come to life',
      'Recognition for innovation and forward-thinking',
      'Autonomy to pursue bold ideas'
    ],
    burnoutWarnings: [
      'Becoming frustrated with "maintenance mode" work',
      'Feeling ideas are constantly shot down',
      'Loss of enthusiasm and future-focus',
      'Withdrawal from strategic conversations'
    ],
    managerPlaybook: [
      'Schedule regular "vision casting" conversations',
      'Protect time for strategic thinking, not just execution',
      'Pair with an Organizer to handle implementation details',
      'Celebrate innovation wins publicly',
      'Give ownership of new initiatives and launches'
    ]
  },
  shepherd: {
    id: 'shepherd',
    name: 'Shepherding Pastor',
    icon: FaHeart,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    shortDescription: 'Pastoral, empathetic, people-focused caregiver',
    fullDescription: "You are deeply empathetic and people-focused. Your greatest fulfillment comes from providing emotional and spiritual support to individuals. People trust you with their burdens because you genuinely care and listen. You have a natural ability to sense when someone is struggling and to create a safe space for them to open up.",
    strengths: [
      'Deep empathy and emotional intelligence',
      'Creating safe, welcoming environments',
      'One-on-one care and counseling',
      'Building trust and lasting relationships',
      'Advocating for individuals\' needs'
    ],
    challenges: [
      'May take on too much emotional weight from others',
      'Can struggle to make tough decisions that might hurt feelings',
      'Might prioritize individual needs over organizational goals',
      'May find it difficult to say no to those in need'
    ],
    leadershipTips: [
      'Ensure opportunities for one-on-one ministry',
      'Check in regularly as they may absorb others\' emotions',
      'Give them roles in pastoral care, small groups, or counseling',
      'Protect them from burnout by monitoring their care load'
    ],
    motivationTips: [
      'Share stories of lives changed through their care',
      'Express personal appreciation for their compassion',
      'Create space for them to process their own emotions',
      'Acknowledge their behind-the-scenes relational work'
    ],
    idealRoles: [
      'Pastoral Care',
      'Small Group Leadership',
      'Counseling Ministry',
      'Hospital/Home Visitation',
      'New Member Integration'
    ],
    communicationDo: [
      'Start with personal connection before business',
      'Ask how they\'re doing and genuinely listen',
      'Acknowledge the people impact of decisions',
      'Give them time to process emotionally'
    ],
    communicationDont: [
      'Rush through relational conversations',
      'Dismiss their concerns about people',
      'Make them feel their care work is "soft" or less valuable',
      'Expect immediate decisions on people-affecting issues'
    ],
    motivationTriggers: [
      'Seeing people healed, restored, or connected',
      'Being trusted with someone\'s story',
      'Personal appreciation from leadership',
      'Time for meaningful 1:1 relationships'
    ],
    burnoutWarnings: [
      'Emotional exhaustion and compassion fatigue',
      'Inability to "turn off" care mode',
      'Neglecting their own needs and boundaries',
      'Feeling like a dumping ground for problems'
    ],
    managerPlaybook: [
      'Regularly check in on their emotional health',
      'Limit their care caseload—quality over quantity',
      'Encourage them to have their own shepherd/mentor',
      'Publicly honor their relational impact',
      'Protect space for rest and recovery'
    ]
  },
  organizer: {
    id: 'organizer',
    name: 'Organizational Administrator',
    icon: FaClipboardList,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    shortDescription: 'Detail-oriented, process-driven planner',
    fullDescription: "Organization and order come naturally to you. You excel at creating systems, plans, and schedules that keep ministry work on track. Your attention to detail ensures nothing falls through the cracks, and you take pride in thoroughness and precision. You bring structure to chaos and help teams operate efficiently.",
    strengths: [
      'Creating efficient systems and processes',
      'Attention to detail and accuracy',
      'Project management and planning',
      'Maintaining consistency and follow-through',
      'Budgeting and resource management'
    ],
    challenges: [
      'May become stressed by too much spontaneity or change',
      'Can be perceived as rigid or inflexible',
      'Might struggle when plans need to be abandoned',
      'May focus too much on process over people'
    ],
    leadershipTips: [
      'Give them clear expectations and structured environments',
      'Trust them with planning and logistics responsibilities',
      'Provide advance notice of changes when possible',
      'Value their input on policies and procedures'
    ],
    motivationTips: [
      'Acknowledge their behind-the-scenes organizational work',
      'Give them ownership of processes and systems',
      'Provide tools and resources to improve efficiency',
      'Celebrate when events run smoothly due to their planning'
    ],
    idealRoles: [
      'Operations Management',
      'Event Coordination',
      'Financial Administration',
      'Database Management',
      'Facilities Oversight'
    ],
    communicationDo: [
      'Be clear, specific, and organized in requests',
      'Provide context and timelines upfront',
      'Give advance notice of changes when possible',
      'Appreciate their attention to detail'
    ],
    communicationDont: [
      'Spring last-minute changes without explanation',
      'Be vague about expectations or deadlines',
      'Dismiss their process concerns as "overthinking"',
      'Expect them to thrive in chaos'
    ],
    motivationTriggers: [
      'Clear expectations and defined processes',
      'Seeing systems run smoothly',
      'Recognition for behind-the-scenes order-keeping',
      'Tools and resources to do their job well'
    ],
    burnoutWarnings: [
      'Constant firefighting with no time for planning',
      'Feeling like their systems are ignored',
      'Overwhelm from too many last-minute changes',
      'Becoming overly controlling or micromanaging'
    ],
    managerPlaybook: [
      'Give them ownership of key systems and processes',
      'Communicate changes early and explain the "why"',
      'Protect their planning time from constant interruptions',
      'Trust their judgment on operational decisions',
      'Pair with Visionaries to balance big-picture and details'
    ]
  },
  evangelist: {
    id: 'evangelist',
    name: 'Outreach Evangelist',
    icon: FaBullhorn,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    shortDescription: 'Outgoing, enthusiastic connector and recruiter',
    fullDescription: "You are energized by meeting new people and introducing others to the church. Networking and outreach feel natural to you, and you have a gift for making strangers feel like friends. Your enthusiasm is contagious, and you excel at rallying people around events, causes, and opportunities to serve.",
    strengths: [
      'Connecting with new people easily',
      'Promoting events and initiatives',
      'Building excitement and momentum',
      'Recruiting volunteers and participants',
      'Public speaking and communication'
    ],
    challenges: [
      'May start many things but struggle to finish them',
      'Can become discouraged by rejection or low response',
      'Might prioritize quantity of connections over depth',
      'May overlook quieter individuals who need attention'
    ],
    leadershipTips: [
      'Give them roles in outreach, welcoming, and promotion',
      'Leverage their networking abilities for recruiting',
      'Pair them with follow-up oriented team members',
      'Provide platforms for their public communication gifts'
    ],
    motivationTips: [
      'Celebrate growth numbers and new connections',
      'Give them visibility and public recognition',
      'Create opportunities to meet and engage new people',
      'Share stories of people they helped connect to the church'
    ],
    idealRoles: [
      'Guest Services/Welcoming',
      'Outreach Coordination',
      'Social Media & Communications',
      'Volunteer Recruitment',
      'Community Events'
    ],
    communicationDo: [
      'Be energetic and enthusiastic in return',
      'Give them opportunities to share and promote',
      'Celebrate wins and new connections together',
      'Keep things moving—they love momentum'
    ],
    communicationDont: [
      'Bog them down in too many details',
      'Keep them isolated or behind-the-scenes for too long',
      'Dismiss their ideas for outreach and growth',
      'Focus only on problems without celebrating progress'
    ],
    motivationTriggers: [
      'Growth numbers and new connections',
      'Public recognition and visibility',
      'Fresh opportunities to engage new people',
      'Being trusted to represent the church externally'
    ],
    burnoutWarnings: [
      'Feeling like outreach efforts aren\'t valued',
      'Lack of new people or growth to celebrate',
      'Being stuck in routine, repetitive tasks',
      'Isolation from external-facing work'
    ],
    managerPlaybook: [
      'Give them the "front door" roles and public platforms',
      'Pair with Shepherds for follow-up and depth',
      'Celebrate growth wins publicly and frequently',
      'Provide variety and new challenges regularly',
      'Trust them to network and recruit externally'
    ]
  },
  teacher: {
    id: 'teacher',
    name: 'Teaching Mentor',
    icon: FaBookOpen,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    shortDescription: 'Knowledge-focused educator and content creator',
    fullDescription: "You have a passion for studying and teaching scripture and Christian principles. You love digging into content, researching deeply, and sharing insights with others. You often serve as the explainer or educator in group settings, and there's nothing more rewarding than seeing someone grasp a concept because of your teaching.",
    strengths: [
      'Deep study and research abilities',
      'Creating curriculum and educational content',
      'Explaining complex concepts clearly',
      'Mentoring and coaching others',
      'Commitment to accuracy and truth'
    ],
    challenges: [
      'May become too academic or theoretical',
      'Can struggle to connect teaching to practical application',
      'Might spend too much time in preparation',
      'May be perceived as know-it-all or condescending'
    ],
    leadershipTips: [
      'Give them teaching and training opportunities',
      'Value their input on content accuracy',
      'Provide resources for continued learning',
      'Pair them with practical-minded implementers'
    ],
    motivationTips: [
      'Acknowledge their expertise and study efforts',
      'Give them challenging intellectual problems',
      'Share feedback from those they\'ve taught',
      'Support their ongoing education and growth'
    ],
    idealRoles: [
      'Adult Education',
      'Curriculum Development',
      'Bible Study Leadership',
      'Staff Training',
      'Discipleship Programs'
    ],
    communicationDo: [
      'Come prepared with context and background',
      'Ask for their expertise and input',
      'Give them time to research and prepare',
      'Value accuracy and depth in discussions'
    ],
    communicationDont: [
      'Rush them into quick answers without research time',
      'Dismiss their need for accuracy as "perfectionism"',
      'Expect them to wing it without preparation',
      'Overlook their teaching contributions'
    ],
    motivationTriggers: [
      'Seeing "lightbulb moments" in others',
      'Being recognized as a subject matter expert',
      'Time for deep study and preparation',
      'Teaching and training opportunities'
    ],
    burnoutWarnings: [
      'No time for study or preparation',
      'Feeling their expertise is undervalued',
      'Teaching without seeing impact or feedback',
      'Being asked to compromise on accuracy'
    ],
    managerPlaybook: [
      'Protect their study and preparation time',
      'Give them ownership of curriculum and training',
      'Ask for their input on theological/content accuracy',
      'Share feedback from those they\'ve taught',
      'Pair with Evangelists for broader reach'
    ]
  },
  servant: {
    id: 'servant',
    name: 'Helpful Servant',
    icon: FaHandsHelping,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    shortDescription: 'Behind-the-scenes, humble, task-oriented helper',
    fullDescription: "Your happiest moments are when you can help someone behind the scenes without any spotlight. You prefer supporting roles over being the center of attention. Humility and service are core values to you, and you show care by doing things for people. You're reliable, loyal, and always ready to jump in wherever help is needed.",
    strengths: [
      'Reliable and consistent follow-through',
      'Willingness to do unglamorous tasks',
      'Humble and team-oriented attitude',
      'Noticing and meeting practical needs',
      'Supporting leaders and initiatives faithfully'
    ],
    challenges: [
      'May not speak up when feeling overwhelmed',
      'Can be overlooked or taken for granted',
      'Might struggle to take initiative or lead',
      'May have difficulty receiving recognition'
    ],
    leadershipTips: [
      'Ensure they don\'t become invisible or overloaded',
      'Publicly appreciate their behind-the-scenes work',
      'Give them clear tasks and expectations',
      'Check in regularly to understand their capacity'
    ],
    motivationTips: [
      'Express personal, specific appreciation',
      'Show how their service makes others\' work possible',
      'Create opportunities for them to serve meaningfully',
      'Don\'t force them into spotlight roles against their nature'
    ],
    idealRoles: [
      'Facilities & Setup',
      'Administrative Support',
      'Hospitality & Catering',
      'Tech Support',
      'Behind-the-Scenes Coordination'
    ],
    communicationDo: [
      'Be specific about what help you need',
      'Express genuine appreciation for their service',
      'Check in on their capacity before adding tasks',
      'Notice and acknowledge their quiet contributions'
    ],
    communicationDont: [
      'Take their help for granted',
      'Force them into public spotlight roles',
      'Overlook their contributions because they don\'t self-promote',
      'Pile on tasks without checking capacity'
    ],
    motivationTriggers: [
      'Knowing their service genuinely helped someone',
      'Personal, specific appreciation from leadership',
      'Being part of a meaningful team effort',
      'Clear tasks where they can see completion'
    ],
    burnoutWarnings: [
      'Silently becoming overloaded without speaking up',
      'Feeling invisible or taken for granted',
      'Resentment building from unacknowledged work',
      'Withdrawal from team activities'
    ],
    managerPlaybook: [
      'Regularly check in—they won\'t always speak up',
      'Publicly acknowledge their behind-the-scenes contributions',
      'Protect them from being everyone\'s "default helper"',
      'Give clear expectations and manageable workloads',
      'Pair with Visionaries or Evangelists who appreciate support'
    ]
  }
};

export const archetypeOrder = [
  'visionary',
  'shepherd',
  'organizer',
  'evangelist',
  'teacher',
  'servant'
];
